import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { BundledAddOnItem, CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { AddOnItemName, BannerCouponName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCountDown } from "features/Home/Banner/useCountdown";
import { accessToPartnershipData } from "utils/influencer";
import {
  formatEpochTimestamp,
  getHoursInFuture,
  isTimeToday,
} from "utils/time";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import { StripeInvoiceLineItem, TabOptions } from "../schema";
import styles from "../styles";
import { useCheckout } from "../useCheckout";
import EmailAddOn from "./CrossSells/EmailAddOn";
import PartnershipDataBundle from "./CrossSells/PartnershipDataBundle";
import PitchingTemplateBundle from "./CrossSells/PitchingTemplateBundle";
import PromotionalCode from "./PromotionalCode";

interface Props {
  displayName: string | undefined;
  productId: string;
  totalDue: number;
}

const InvoicePreview = ({ displayName, productId, totalDue }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { tab, setTab, subscription, plans } = useContext(SubscriptionContext);
  const { display, createTimer } = useCountDown();
  const { getPromoCodeByName } = useCheckout();
  const {
    addOnItems,
    applyAddOnLoading,
    invoice,
    customer,
    setPlan,
    plan,
    splashPageSource,
    invoiceLoading,
  } = useContext(CheckoutContext);

  const { applyAddOnItem, removePromoCode, isAddOnItem, updatePreviewInvoice } =
    useCheckout();

  // Variables
  const hasDiscount = invoice?.discounts?.find(
    (x) => x.promotion_code && x.promotion_code?.length > 0,
  );

  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );
  const displayProBundle =
    (plan === Plans.PRO || plan === Plans.PRO_YEARLY) &&
    splashPageSource !== SplashPage.pro_bundle;

  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const isProBundleFlow = splashPageSource === SplashPage.pro_bundle;
  const isVaFlow = splashPageSource === SplashPage.va_money_back;
  const taxAmount = invoice?.total_taxes?.[0]?.amount;

  const displayVaBundle =
    (plan === Plans.VA || plan === Plans.VA_YEARLY) &&
    splashPageSource !== SplashPage.va_bundled;

  const displayAutopilotBundle =
    plan === Plans.AUTOPILOT || plan === Plans.AUTOPILOT_YEARLY;

  const canRemoveCoupon =
    !splashPageSource ||
    ![SplashPage.ooo, SplashPage.pro_bundle]?.includes(splashPageSource);

  const switchTab = async (newTab: SubscriptionTab) => {
    if (tab === newTab) return;
    trackEvent("Invoice Preview - Switch Subscription Tab", { tab: newTab });
    setTab(newTab);

    let alternativePlan =
      plan && plan in TabOptions
        ? TabOptions[plan as keyof typeof TabOptions]
        : null;

    const emailPlans = [
      AddOnItemName.email_volume_monthly,
      AddOnItemName.email_volume_yearly,
    ];
    const emailProPlans = [
      AddOnItemName.email_volume_pro_monthly,
      AddOnItemName.email_volume_pro_yearly,
    ];
    const isAddOnEmail = emailPlans.includes(plan as AddOnItemName);

    if (isAddOnEmail) {
      const copy = makeDeepCopy(addOnItems || []);
      const index = copy.findIndex((x: BundledAddOnItem) =>
        emailPlans.includes(x.name as AddOnItemName),
      );
      const item = {
        name: alternativePlan as AddOnItemName,
        quantity: index !== -1 ? copy[index].quantity : 1,
      };
      if (index !== -1) {
        // If quantity is 0, remove the item from the list
        copy.splice(index, 1);
      }
      copy.push(item);
      setPlan(alternativePlan as Plans | AddOnItemName);
      await updatePreviewInvoice({
        plan: alternativePlan as Plans | AddOnItemName,
        addOnItems: copy,
      });
    } else if (alternativePlan) {
      // Swap pro email volume add-on items when switching tabs on a Pro plan
      const copy = makeDeepCopy(addOnItems || []);
      const proEmailIndex = copy.findIndex((x: BundledAddOnItem) =>
        emailProPlans.includes(x.name as AddOnItemName),
      );
      if (proEmailIndex !== -1) {
        const proEmailAlternative =
          copy[proEmailIndex].name === AddOnItemName.email_volume_pro_monthly
            ? AddOnItemName.email_volume_pro_yearly
            : AddOnItemName.email_volume_pro_monthly;
        copy[proEmailIndex] = {
          ...copy[proEmailIndex],
          name: proEmailAlternative,
        };
        await updatePreviewInvoice({
          plan: alternativePlan,
          addOnItems: copy,
        });
        setPlan(alternativePlan);
        return;
      }
      await updatePreviewInvoice({
        plan: alternativePlan,
      });
      setPlan(alternativePlan);
    }
  };

  const getItemName = (row: StripeInvoiceLineItem) => {
    return row?.metadata?.item_name;
  };

  const renderDescription = (description: string) => {
    if (plans && plan && plans[plan as Plans]?.price_annotation) {
      return `${description} ${plans[plan as Plans]?.price_annotation}`;
    }
    if (description.startsWith("1 × ")) {
      return description.slice(4);
    }
    return description;
  };

  const renderBundle = () => {
    if (displayVaBundle) {
      return (
        <Box>
          {[
            tab === SubscriptionTab.yearly
              ? AddOnItemName.email_volume_yearly
              : AddOnItemName.email_volume_monthly,
          ].map((addOnItemName) => (
            <Box key={addOnItemName} sx={{ mt: 2 }}>
              <EmailAddOn addOnItemName={addOnItemName} />
            </Box>
          ))}
        </Box>
      );
    }

    if (displayProBundle) {
      return (
        <>
          <Box sx={{ mt: 2 }}>
            <EmailAddOn
              addOnItemName={
                tab === SubscriptionTab.yearly
                  ? AddOnItemName.email_volume_pro_yearly
                  : AddOnItemName.email_volume_pro_monthly
              }
            />
          </Box>
          {!hasAccessToPartnershipData && <PartnershipDataBundle />}
          <PitchingTemplateBundle />
        </>
      );
    }

    if (displayAutopilotBundle) {
      return (
        <>
          <Box sx={{ mt: 2 }}>
            <EmailAddOn
              addOnItemName={
                tab === SubscriptionTab.yearly
                  ? AddOnItemName.email_volume_pro_yearly
                  : AddOnItemName.email_volume_pro_monthly
              }
            />
          </Box>
          <PitchingTemplateBundle />
        </>
      );
    }
    return <></>;
  };

  useEffect(() => {
    const isYearlyPlan =
      plan &&
      [Plans.VA_YEARLY, Plans.PRO_YEARLY, Plans.AUTOPILOT_YEARLY]?.includes(
        plan as Plans,
      );
    if (isYearlyPlan && tab !== SubscriptionTab.yearly) {
      setTab(SubscriptionTab.yearly);
    }
    if (
      plan === Plans.VA_YEARLY &&
      splashPageSource === SplashPage.va_money_back
    ) {
      const bannerPromoCode = getPromoCodeByName(
        BannerCouponName.money_back_guarantee,
      );
      if (bannerPromoCode) {
        const countdownDate = getHoursInFuture(10);
        createTimer(bannerPromoCode, countdownDate?.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, splashPageSource]);

  const getNextPayDate = () => {
    const nextPaymentDate =
      invoice?.next_payment_attempt || invoice?.period_start;
    if (!nextPaymentDate) return "";
    const isTimeStampToday = isTimeToday(nextPaymentDate);
    if (isTimeStampToday) {
      return <>Total due today</>;
    } else {
      return `Total due on ${formatEpochTimestamp({
        epochTimestamp: nextPaymentDate,
        addYear: true,
      })}`;
    }
  };

  const hideTab =
    splashPageSource === SplashPage.va_bundled
      ? false
      : isOOOFlow ||
        isProBundleFlow ||
        isVaFlow ||
        splashPageSource === SplashPage.annual ||
        !(plan && plan in TabOptions);

  const discountAmount =
    Number(invoice?.total_discount_amounts?.[0]?.amount || 0) / 100;
  const originalAmount = (discountAmount || 0) + totalDue;

  return (
    <Box>
      {!hideTab && (
        <Box sx={styles.tabContainer}>
          <Link
            sx={[
              styles.tab,
              tab === SubscriptionTab.monthly ? styles.active : {},
            ]}
            onClick={() => switchTab(SubscriptionTab.monthly)}
          >
            Monthly
          </Link>
          <Link
            sx={[
              styles.tab,
              tab === SubscriptionTab.yearly ? styles.active : {},
            ]}
            onClick={() => switchTab(SubscriptionTab.yearly)}
          >
            Yearly
            <Box sx={styles.annotation}>
              <Box
                component="i"
                className="fa-regular fa-arrow-down"
                sx={{ mr: 0.5 }}
              />{" "}
              25% {isAddOnItem ? "off" : "+ Free Template"}
            </Box>
          </Link>
        </Box>
      )}

      <Table
        size="small"
        sx={{ mt: 2, "& td, & th": { borderBottom: "none" } }}
      >
        <TableBody>
          {invoice?.lines?.data?.map((row, index) => (
            <TableRow key={index}>
              <TableCell
                component="th"
                scope="row"
                sx={{ fontSize: 14, color: "black" }}
              >
                {renderDescription(row.description)}
                {plan === AddOnItemName.custom_email && index === 1 && (
                  <ul
                    style={{
                      color: grey[500],
                      margin: 0,
                      marginTop: "4px",
                      paddingLeft: "20px",
                    }}
                  >
                    <li>Gmail inbox with your domain</li>
                    <li>Monthly hosting</li>
                    <li>Deliverability setup (SPF, DKIM, DMARC)</li>
                  </ul>
                )}

                {plan !== AddOnItemName.email_volume_monthly &&
                  plan !== AddOnItemName.email_volume_yearly &&
                  plan !== AddOnItemName.pitching_templates &&
                  (splashPageSource === SplashPage.va_bundled ||
                    (!isProBundleFlow &&
                      getItemName(row) &&
                      getItemName(row) !== AddOnItemName.oui_studio)) && (
                    <Tooltip title="Remove add-on" placement="right">
                      <Box
                        component="span"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await applyAddOnItem({
                            name: getItemName(row) as AddOnItemName,
                            quantity: 0,
                          });
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 18,
                          height: 18,
                          ml: 0.75,
                          borderRadius: "50%",
                          cursor:
                            applyAddOnLoading === getItemName(row)
                              ? "wait"
                              : "pointer",
                          color: grey[400],
                          border: `1px solid ${grey[300]}`,
                          transition:
                            "color 0.15s, border-color 0.15s, background-color 0.15s",
                          verticalAlign: "middle",
                          "&:hover": {
                            color: "#DC2626",
                            borderColor: "#FCA5A5",
                            backgroundColor: "#FEF2F2",
                          },
                        }}
                      >
                        <Box
                          component="i"
                          className={
                            applyAddOnLoading === getItemName(row)
                              ? "fa-regular fa-spinner-third fa-spin"
                              : "fa-regular fa-xmark"
                          }
                          sx={{ fontSize: 9 }}
                        />
                      </Box>
                    </Tooltip>
                  )}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 14, color: "black" }}>
                ${Number(row.amount / 100).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          {displayVaBundle && (
            <TableRow>
              <TableCell component="th" scope="row">
                300+ automated pitches/month
              </TableCell>

              <TableCell align="right" sx={{ color: "#4A5565" }}>
                $0.00
              </TableCell>
            </TableRow>
          )}

          {isOOOFlow && currentOrg?.isVaUser && (
            <TableRow>
              <TableCell component="th" scope="row">
                You already subscribed to Virtual Assistant
              </TableCell>

              <TableCell align="right" sx={{ color: "#4A5565" }}>
                $0.00
              </TableCell>
            </TableRow>
          )}

          {(plan === Plans.PRO_YEARLY ||
            plan === Plans.VA_YEARLY ||
            plan === Plans.AUTOPILOT_YEARLY) && (
            <TableRow>
              <TableCell component="th" scope="row" sx={{ color: "#4A5565" }}>
                1 × Free Template Coupon 🗑️
              </TableCell>

              <TableCell align="right" sx={{ color: "#4A5565" }}>
                $0.00
              </TableCell>
            </TableRow>
          )}

          {taxAmount && taxAmount > 0 ? (
            <TableRow>
              <TableCell component="th" scope="row">
                Tax ({customer?.address?.country})
              </TableCell>
              <TableCell align="right">
                ${Number(taxAmount / 100).toFixed(2)}
              </TableCell>
            </TableRow>
          ) : (
            <></>
          )}
        </TableBody>
      </Table>

      {invoice?.total_discount_amounts?.map((row, index) => (
        <Grid
          item
          xs={12}
          key={index}
          sx={{ px: 2, py: 1, fontSize: 12, mt: 2 }}
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            disableElevation
            size="small"
            variant="contained"
            sx={styles.coupon}
            onClick={() => {
              if (canRemoveCoupon) {
                removePromoCode();
              }
            }}
          >
            {invoice?.discounts?.[index]?.coupon?.name}

            {canRemoveCoupon && invoice?.discounts?.[index]?.promotion_code && (
              <Box
                component="i"
                className="fa-regular fa-xmark"
                sx={{ ml: 1 }}
              />
            )}
          </Button>

          <Box sx={{ color: grey[500] }}>
            -${Number(row.amount / 100).toFixed(2)}
          </Box>
        </Grid>
      ))}

      {invoice && invoice?.starting_balance !== 0 && (
        <Grid
          item
          xs={12}
          sx={{ px: 2, py: 1, fontSize: 12 }}
          container
          justifyContent="space-between"
        >
          <Box>Credit Balance </Box>
          <Box sx={{ color: grey[500] }}>
            -${Number(Math.abs(invoice?.starting_balance / 100)).toFixed(2)}
          </Box>
        </Grid>
      )}

      <Divider sx={{ my: 2, borderColor: "purple.light" }} />

      <Table
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableBody
          sx={{
            "&:last-child tr, &:last-child th": { border: 0 },
          }}
        >
          <TableRow>
            {(invoice?.next_payment_attempt || invoice?.period_start) && (
              <TableCell component="th" scope="row" sx={{ fontSize: 14 }}>
                {getNextPayDate()}
              </TableCell>
            )}
            <TableCell component="th" scope="row" align="right">
              {invoiceLoading ? (
                <Grid container justifyContent={"flex-end"}>
                  <Skeleton animation="wave" width={150} height={30} />
                </Grid>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    {/* Discounted total */}
                    <Box
                      sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        $
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 26,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: "text.primary",
                        }}
                      >
                        {totalDue.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.secondary",
                          textTransform: "uppercase",
                          lineHeight: 1,
                        }}
                      >
                        {invoice?.currency}
                      </Typography>
                    </Box>

                    {/* Original price + savings badge */}
                    {originalAmount && originalAmount > totalDue && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "text.disabled",
                            textDecoration: "line-through",
                          }}
                        >
                          ${originalAmount.toFixed(2)}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#15803D",
                            backgroundColor: "#F0FDF4",
                            border: "1px solid #BBF7D0",
                            borderRadius: "5px",
                            px: "6px",
                            py: "2px",
                            lineHeight: 1.4,
                          }}
                        >
                          Save ${(originalAmount - totalDue).toFixed(2)}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {display && splashPageSource === SplashPage.va_money_back && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "info.main",
                        textTransform: "none",
                      }}
                    >
                      ⏳ Additional 10% off for the next{" "}
                      <strong>{display}</strong>
                    </Typography>
                  )}
                </>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {tab === SubscriptionTab.monthly &&
        invoice &&
        !invoiceLoading &&
        !isMobileScreen &&
        [Plans.VA, Plans.PRO, Plans.AUTOPILOT]?.includes(plan as Plans) && (
          <Box
            onClick={() => {
              trackEvent(
                "Check Out Page - Get a Free Pitch Template with Annual Plan",
              );
              switchTab(SubscriptionTab.yearly);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 2,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              backgroundColor: "#FAF5FF",
              border: "1px solid #E9D5FF",
              cursor: "pointer",
              transition:
                "background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": {
                backgroundColor: "#F3E8FF",
                borderColor: "#D8B4FE",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  fontSize: 15,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                🎁
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6B21A8",
                    lineHeight: 1.3,
                  }}
                >
                  Free pitch template included
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#9333EA",
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  Switch to Annual and get a template worth $24
                </Typography>
              </Box>
            </Box>
            <Box
              component="i"
              className="fa-regular fa-arrow-right"
              sx={{ fontSize: 12, color: "#9333EA", flexShrink: 0 }}
            />
          </Box>
        )}

      {!hasDiscount && (
        <Grid container justifyContent="flex-end">
          <PromotionalCode productId={productId} />
        </Grid>
      )}
      {!isMobileScreen && <>{renderBundle()}</>}
    </Box>
  );
};

export default InvoicePreview;
